/**
 * ParameterTrioInput Component
 * 
 * A trio of inputs for parameter name, current value, and target value.
 * 
 * @param {string} label - Label for the parameter group
 * @param {object} value - Object with { name, current, target }
 * @param {function} onChange - Callback when any value changes, receives updated object
 * @param {string} namePlaceholder - Placeholder for name input
 * @param {string} currentPlaceholder - Placeholder for current value input
 * @param {string} targetPlaceholder - Placeholder for target value input
 * @param {string} color - Accent color: "blue", "rose", "amber", "indigo" (default: "indigo")
 * @param {string} unit - Unit to display (e.g., "mm", "cm")
 */

const COLOR_CLASSES = {
  blue: "focus:border-blue-500 focus:ring-blue-500",
  rose: "focus:border-rose-500 focus:ring-rose-500",
  amber: "focus:border-amber-500 focus:ring-amber-500",
  indigo: "focus:border-indigo-500 focus:ring-indigo-500",
};

export default function ParameterTrioInput({
  label = null,
  value = { name: "", current: "", target: "" },
  onChange,
  namePlaceholder = "Parameter name",
  currentPlaceholder = "Current",
  targetPlaceholder = "Target",
  color = "indigo",
  unit = "",
}) {
  const focusColor = COLOR_CLASSES[color] || COLOR_CLASSES.indigo;

  const handleChange = (field, newValue) => {
    onChange({
      ...value,
      [field]: newValue,
    });
  };

  const inputBaseClass = `
    w-full px-3 py-2.5 rounded-lg border text-sm text-gray-900
    placeholder:text-gray-400 transition-colors duration-200
    border-gray-300 ${focusColor} focus:ring-2 focus:ring-opacity-20
    bg-white
  `;

  return (
    <div className="space-y-2">
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Trio of inputs */}
      <div className="grid grid-cols-3 gap-3">
        {/* Name input */}
        <div>
          <span className="block text-xs text-gray-500 mb-1">Name</span>
          <input
            type="text"
            value={value.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder={namePlaceholder}
            className={inputBaseClass}
          />
        </div>

        {/* Current value input */}
        <div>
          <span className="block text-xs text-gray-500 mb-1">
            Current {unit && <span className="text-gray-400">({unit})</span>}
          </span>
          <input
            type="number"
            step="0.1"
            value={value.current}
            onChange={(e) => handleChange("current", e.target.value)}
            placeholder={currentPlaceholder}
            className={inputBaseClass}
          />
        </div>

        {/* Target value input */}
        <div>
          <span className="block text-xs text-gray-500 mb-1">
            Target {unit && <span className="text-gray-400">({unit})</span>}
          </span>
          <input
            type="number"
            step="0.1"
            value={value.target}
            onChange={(e) => handleChange("target", e.target.value)}
            placeholder={targetPlaceholder}
            className={inputBaseClass}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * ParameterTrioGroup Component
 * 
 * A group of ParameterTrioInputs with a section title.
 * 
 * @param {string} title - Section title
 * @param {string} description - Section description
 * @param {Array} parameters - Array of parameter objects
 * @param {function} onChange - Callback when any parameter changes, receives (index, value)
 * @param {string} color - Accent color
 * @param {string} unit - Unit for values
 */
ParameterTrioInput.Group = function ParameterTrioGroup({
  title,
  description = null,
  parameters = [],
  onChange,
  color = "indigo",
  unit = "",
}) {
  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-sm font-medium text-gray-700">{title}</h4>
        {description && (
          <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
      
      <div className="space-y-3">
        {parameters.map((param, index) => (
          <ParameterTrioInput
            key={index}
            value={param}
            onChange={(newValue) => onChange(index, newValue)}
            color={color}
            unit={unit}
          />
        ))}
      </div>
    </div>
  );
};